import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Drawer, Dropdown, Form, Grid, Input, Layout, Menu, Modal, Typography, message } from 'antd';
import {
  AppstoreOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  ImportOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { LoginContext } from '../../context/loginContext';
import { usePermissions } from '../../context/permissionsContext';
import api from '../../api/client';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;
const SIDER_COLLAPSED_WIDTH = 64;
const SIDER_EXPANDED_WIDTH = 190;

const allMenuItems = [
  { key: '/administrador/home', icon: <HomeOutlined />, label: 'Inicio', permission: ['inicio', 'ver'] },
  { key: '/administrador/clientes', icon: <TeamOutlined />, label: 'Clientes', permission: ['clientes', 'ver'] },
  { key: '/administrador/productos', icon: <AppstoreOutlined />, label: 'Productos o Servicios', permission: ['productos', 'ver'] },
  { key: '/administrador/conceptos', icon: <FileTextOutlined />, label: 'Conceptos', permission: ['conceptos', 'ver'] },
  { key: '/administrador/exportacion', icon: <ExportOutlined />, label: 'Exportacion', permission: ['exportacion', 'ver'] },
  { key: '/administrador/importacion', icon: <ImportOutlined />, label: 'Importación', permission: ['importacion', 'ver'] },
  {
    key: '/administrador/servicios-acordados',
    icon: <FileProtectOutlined />,
    label: 'Servicios acordados',
    permission: ['servicios_acordados', 'ver'],
  },
  {
    key: '/administrador/cotizaciones-eliminadas',
    icon: <DeleteOutlined />,
    label: 'Cotizaciones eliminadas',
    permission: ['cotizaciones_eliminadas', 'ver'],
  },
  {
    key: '/administrador/agenda-reuniones',
    icon: <CalendarOutlined />,
    label: 'Agenda de Reuniones',
    permission: ['agenda_reuniones', 'ver'],
  },
  {
    key: '/administrador/curriculums',
    icon: <FolderOpenOutlined />,
    label: 'Curriculums',
    permission: ['curriculums', 'ver'],
  },
  {
    key: '/administrador/localidades',
    icon: <EnvironmentOutlined />,
    label: 'Localidades',
    permission: ['localidades', 'ver'],
  },
  { key: '/administrador/usuarios', icon: <UserOutlined />, label: 'Usuarios', permission: ['usuarios', 'ver'] },
  { key: '/administrador/roles', icon: <SafetyCertificateOutlined />, label: 'Roles', permission: ['usuarios', 'ver'] },
];

const MainLayout = ({ children }) => {
  const { user, logout, login } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [siderHovered, setSiderHovered] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdForm] = Form.useForm();

  const { hasPermission } = usePermissions();

  const role = String(user?.tiu_descripcion || '').toUpperCase();
  const menuKeys = allMenuItems
    .filter((item) => {
      if (!item.permission) return true;
      const [modulo, accion] = item.permission;
      return hasPermission(modulo, accion);
    })
    .map(({ key, icon, label }) => ({ key, icon, label }));

  const handleCambiarPassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setPwdSubmitting(true);
      const { data } = await api.patch('/usuarios/me/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      login({
        ...data.usuario,
        token: data.token,
        refreshToken: data.refreshToken,
        id: data.usuario.usu_id,
      });
      message.success(data.message || 'Contraseña actualizada');
      setPwdModalOpen(false);
      pwdForm.resetFields();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'No se pudo actualizar la contraseña');
    } finally {
      setPwdSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Limpieza local aunque falle backend
    }
    logout();
    navigate('/login', { replace: true });
  };

  const onMenuNavigate = ({ key }) => {
    navigate(key);
    setDrawerOpen(false);
    setSiderHovered(false);
  };

  const renderMenu = (menuCollapsed = false) => (
    <>
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: menuCollapsed ? 'center' : 'flex-start',
          paddingInline: menuCollapsed ? 0 : 12,
          borderBottom: '1px solid #d8e8dc',
        }}
      >
        <Text style={{ color: '#198039', fontSize: menuCollapsed ? 16 : 14, fontWeight: 700 }}>
          {menuCollapsed ? 'G' : 'Global Admin'}
        </Text>
      </div>
      <Menu
        theme="light"
        mode="inline"
        inlineCollapsed={menuCollapsed}
        selectedKeys={[location.pathname]}
        items={menuKeys}
        onClick={onMenuNavigate}
        style={{ marginTop: 4, borderRight: 0, fontSize: 12 }}
      />
    </>
  );

  return (
    <>
      <Layout style={{ minHeight: '100vh' }}>
        {!isMobile && (
          <div
            style={{
              position: 'relative',
              flex: `0 0 ${SIDER_COLLAPSED_WIDTH}px`,
              maxWidth: SIDER_COLLAPSED_WIDTH,
              minHeight: '100vh',
              zIndex: 200,
            }}
            onMouseEnter={() => setSiderHovered(true)}
            onMouseLeave={() => setSiderHovered(false)}
          >
            <Sider
              theme="light"
              collapsed
              trigger={null}
              width={SIDER_EXPANDED_WIDTH}
              collapsedWidth={SIDER_COLLAPSED_WIDTH}
              style={{ background: '#ffffff', minHeight: '100vh', borderRight: '1px solid #d8e8dc' }}
            >
              {renderMenu(true)}
            </Sider>
            {siderHovered && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: SIDER_EXPANDED_WIDTH,
                  minHeight: '100vh',
                  background: '#ffffff',
                  borderRight: '1px solid #d8e8dc',
                  boxShadow: '2px 0 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                {renderMenu(false)}
              </div>
            )}
          </div>
        )}
        <Layout style={{ minWidth: 0 }}>
          <Header
            style={{
              background: '#ffffff',
              borderBottom: '1px solid #d8e8dc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: 48,
              lineHeight: '48px',
              paddingInline: isMobile ? 8 : 10,
              gap: 8,
              position: 'sticky',
              top: 0,
              zIndex: 100,
            }}
          >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 18 }} />}
                onClick={() => setDrawerOpen(true)}
                aria-label="Abrir menu"
              />
            )}
            <Text
              ellipsis
              style={{ color: '#198039', fontSize: 12, flex: isMobile ? 1 : undefined }}
            >
              {role || 'USUARIO'}
            </Text>
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => setPwdModalOpen(true)}
            style={{
              color: '#198039',
              fontSize: isMobile ? 11 : 12,
              paddingInline: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Cambiar contraseña
          </Button>
          <Dropdown
            menu={{
              items: [
                { key: 'password', icon: <LockOutlined />, label: 'Cambiar contraseña' },
                { type: 'divider' },
                { key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar sesion', danger: true },
              ],
              onClick: ({ key }) => {
                if (key === 'password') setPwdModalOpen(true);
                if (key === 'logout') void handleLogout();
              },
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              style={{
                color: '#166534',
                fontSize: 12,
                paddingInline: 6,
                maxWidth: isMobile ? 140 : undefined,
              }}
            >
              <Avatar size={20} icon={<UserOutlined />} />
              <span style={{ marginLeft: 6, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.usu_usuario || 'Usuario'}
              </span>
            </Button>
          </Dropdown>
        </Header>
        <Content
          style={{
            padding: isMobile ? '8px 8px 16px' : 12,
            background: '#f3faf6',
            minHeight: 'calc(100vh - 48px)',
          }}
        >
          {children}
        </Content>
        </Layout>
      </Layout>

      <Drawer
        title={null}
        placement="left"
        width={280}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{ body: { padding: 0 } }}
        destroyOnClose={false}
      >
        {renderMenu(false)}
      </Drawer>

      <Modal
        title="Cambiar contraseña"
        open={pwdModalOpen}
        onCancel={() => {
          setPwdModalOpen(false);
          pwdForm.resetFields();
        }}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={pwdSubmitting}
        onOk={() => void handleCambiarPassword()}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
          Ingresá tu contraseña actual y la nueva contraseña (mínimo 6 caracteres).
        </Typography.Paragraph>
        <Form form={pwdForm} layout="vertical" requiredMark={false}>
          <Form.Item
            name="currentPassword"
            label="Contraseña actual"
            rules={[{ required: true, message: 'Ingresá tu contraseña actual' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="Nueva contraseña"
            rules={[
              { required: true, message: 'Ingresá la nueva contraseña' },
              { min: 6, message: 'Mínimo 6 caracteres' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Repetir nueva contraseña"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Repetí la nueva contraseña' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Las contraseñas no coinciden'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MainLayout;
